import React, { useEffect, useState } from "react";
import { PaStatusBadge } from "components/platform-admin";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import { formatGhostElapsed, formatGhostRoleLabel } from "pages/PlatformAdmin/Ghost/ghostSession.utils";

const GhostModeBanner = () => {
  const { ghostSession, tenantContext, endGhost, isGhostActive } = usePlatformAdmin();
  const [elapsed, setElapsed] = useState("0:00:00");

  useEffect(() => {
    if (!isGhostActive || !ghostSession?.startedAt) {
      return undefined;
    }

    const tick = () => {
      setElapsed(formatGhostElapsed(ghostSession.startedAt));
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [ghostSession?.startedAt, isGhostActive]);

  if (!isGhostActive || !ghostSession?.actingUser) {
    return null;
  }

  const actingUser = ghostSession.actingUser;
  const tenantFlags = ghostSession.tenantFlags || {};
  const tenantName = ghostSession.spName || tenantContext?.spName;
  const displayName = actingUser.name || actingUser.email;
  const roleLabel = formatGhostRoleLabel(actingUser.user_type);

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 px-4 py-2 sm:px-6 lg:px-8"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200 text-xs font-bold uppercase tracking-wide text-amber-900"
            aria-hidden="true"
          >
            Gh
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-950">
              Ghost session
              {tenantName ? (
                <>
                  {" "}
                  in <span className="text-amber-900">{tenantName}</span>
                </>
              ) : null}
            </p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-900/90">
              <span>
                Viewing as <span className="font-medium text-amber-950">{displayName}</span>
                {roleLabel ? <> · {roleLabel}</> : null}
              </span>
              <span className="hidden text-amber-400 sm:inline" aria-hidden="true">
                |
              </span>
              <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[11px] text-amber-950">
                {elapsed}
              </span>
              {tenantFlags.suspended ? (
                <PaStatusBadge label="Tenant suspended" variant="danger" />
              ) : null}
              {tenantFlags.read_only ? (
                <PaStatusBadge label="Read-only" variant="warning" />
              ) : null}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => endGhost()}
          className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-950 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          End session
        </button>
      </div>
    </div>
  );
};

export default GhostModeBanner;
